import axios from "axios";
import axioInstance from "../../../custom_utilities/AxiosInstance/axiosInstance";
import { store } from "../../../redux/store/store";
import { queryClient } from "../../../app/queryClient";
import {
  studyDashboardAssessmentQueryKeys,
  studyDashboardQueryKeys,
  studyDashboardResumeQueryKey,
} from "./studyDashboardQueryKeys";

export const DASHBOARD_STALE_TIME = 5 * 60 * 1000;
export const DASHBOARD_CACHE_TIME = 30 * 60 * 1000;
/** Weak topics and subject performance change mainly after an exam. */
export const DASHBOARD_SLOW_ANALYTICS_STALE_TIME = 30 * 60 * 1000;
export const DASHBOARD_SLOW_ANALYTICS_CACHE_TIME = 60 * 60 * 1000;

const httpMissUntil = new Map();

const missKey = (queryKey) => JSON.stringify(queryKey);

const prefixPattern = (queryKeyPrefix) =>
  JSON.stringify(queryKeyPrefix).slice(0, -1);

export const clearDashboardHttpMisses = (queryKeyPrefix) => {
  if (!queryKeyPrefix) {
    httpMissUntil.clear();
    return;
  }

  const prefix = prefixPattern(queryKeyPrefix);
  httpMissUntil.forEach((_until, key) => {
    if (key.startsWith(prefix)) httpMissUntil.delete(key);
  });
};

/**
 * Remembers HTTP 404s for staleTime so a missing endpoint is not requested
 * again on every dashboard remount. The error is still thrown, so the query
 * stays in the error state instead of looking like a successful empty payload.
 */
export const runDashboardQuery = async (queryKey, queryFn) => {
  const key = missKey(queryKey);
  const until = httpMissUntil.get(key);
  if (until && Date.now() < until) {
    const error = new Error("Request failed with status code 404");
    error.response = { status: 404 };
    error.isCachedDashboardMiss = true;
    throw error;
  }

  try {
    const data = await queryFn();
    httpMissUntil.delete(key);
    return data;
  } catch (error) {
    if (error?.response?.status === 404) {
      httpMissUntil.set(key, Date.now() + DASHBOARD_STALE_TIME);
    }
    throw error;
  }
};

export const retryDashboardQuery = (failureCount, error) => {
  const status = error?.response?.status;
  if (
    error?.isCachedDashboardMiss ||
    status === 400 ||
    status === 401 ||
    status === 403 ||
    status === 404
  ) {
    return false;
  }
  return failureCount < 1;
};

const invalidatePrefixes = (prefixes) => {
  prefixes.forEach((queryKey) => {
    clearDashboardHttpMisses(queryKey);
    queryClient.invalidateQueries({ queryKey, refetchType: "all" });
  });
};

export const invalidateStudyDashboardAfterAssessment = () => {
  invalidatePrefixes(studyDashboardAssessmentQueryKeys);
};

export const invalidateStudyDashboardResume = () => {
  invalidatePrefixes([studyDashboardResumeQueryKey]);
};

const SUBMIT_MARKERS = [
  "/exam_submit",
  "/examsubmit",
  "/UserOtsSubmit",
  "/UserPYQSubmit",
  "/UserCPSSubmit",
  "/UserQBankSubmit",
  "/examcracker/submit",
  "/ots/user/final/submitoptimized",
  "/homework_submit_details",
];

const isAssessmentSubmitUrl = (url) => {
  const value = String(url || "");
  return SUBMIT_MARKERS.some((marker) => value.includes(marker));
};

const isSuccessfulSubmit = (response) => {
  const httpStatus = response?.status;
  if (httpStatus < 200 || httpStatus >= 300) return false;

  const bodyStatus = response?.data?.status;
  if (bodyStatus == null || bodyStatus === "") return true;
  if (bodyStatus === 0 || bodyStatus === false || bodyStatus === "0") return false;
  if (typeof bodyStatus === "number" && bodyStatus >= 400) return false;
  return true;
};

const attachAssessmentInvalidation = (client) => {
  if (client.__studyDashboardAssessmentSync) return;
  client.__studyDashboardAssessmentSync = true;

  client.interceptors.response.use((response) => {
    if (
      isAssessmentSubmitUrl(response?.config?.url) &&
      isSuccessfulSubmit(response)
    ) {
      invalidateStudyDashboardAfterAssessment();
    }
    return response;
  });
};

const VIDEO_ROUTE_PREFIXES = ["/videos", "/streaming", "/recorded", "/zoom"];

const isVideoRoute = (path) =>
  VIDEO_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );

let currentPath = typeof window !== "undefined" ? window.location.pathname : "";
let previousPath = "";
let consumedResumeRefresh = "";

const rememberPath = () => {
  if (typeof window === "undefined") return;
  const nextPath = window.location.pathname;
  if (!nextPath || nextPath === currentPath) return;
  previousPath = currentPath;
  currentPath = nextPath;
};

const watchClientNavigation = () => {
  if (typeof window === "undefined" || window.__studyDashboardPathWatch) return;
  window.__studyDashboardPathWatch = true;

  const wrap = (methodName) => {
    const original = window.history[methodName];
    if (typeof original !== "function") return;
    window.history[methodName] = function wrappedHistoryMethod(...args) {
      const result = original.apply(this, args);
      rememberPath();
      return result;
    };
  };

  wrap("pushState");
  wrap("replaceState");
  window.addEventListener("popstate", rememberPath);
};

export const refreshResumeAfterVideoRoute = () => {
  if (!isVideoRoute(previousPath)) return;
  const marker = `${previousPath}->${currentPath}`;
  if (consumedResumeRefresh === marker) return;
  consumedResumeRefresh = marker;
  invalidateStudyDashboardResume();
};

let syncStarted = false;

export const ensureStudyDashboardAssessmentSync = () => {
  if (syncStarted) return;
  syncStarted = true;

  attachAssessmentInvalidation(axios);
  attachAssessmentInvalidation(axioInstance);
  watchClientNavigation();

  let lastToken = store.getState()?.auth?.authToken || "";
  store.subscribe(() => {
    const nextToken = store.getState()?.auth?.authToken || "";
    if (lastToken && !nextToken) {
      clearDashboardHttpMisses();
      queryClient.removeQueries({ queryKey: studyDashboardQueryKeys.root });
    }
    lastToken = nextToken;
  });
};
